import React, { useState, useEffect } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import HomeIcon from "@mui/icons-material/Home";
import LogoutIcon from "@mui/icons-material/Logout";
import AccountCircleOutlinedIcon from "@mui/icons-material/AccountCircleOutlined";
import Button from "@mui/material/Button";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import PersonIcon from "@mui/icons-material/Person";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SubscriptionsIcon from "@mui/icons-material/Subscriptions";
import { logoutApiCall } from "../../../utils/Apicalls";
import env from "../../../utils/env";
import "./Header.css";

function Header() {
  const [isNavCollapsed, setIsNavCollapsed] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [profileName, setProfileName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();

  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);

  const handleNavCollapse = () => setIsNavCollapsed(!isNavCollapsed);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  useEffect(() => {
    setProfileName("");
    // Check if user is logged in
    const userDetails = localStorage.getItem("userDetails");
    if (userDetails) {
      setIsLoggedIn(true);

      // Check if user is admin
      try {
        const userData = JSON.parse(userDetails);
        let name = userData?.first_name || "";
        if (userData?.last_name) {
          name += " " + userData?.last_name;
        }
        setProfileName(name);
        if (["admin", "sales", "tennant"].indexOf(userData.role_id) !== -1) {
          setIsAdmin(true);
        }
      } catch (error) {
        console.error("Error parsing user data:", error);
      }
    }
    console.log("Header mounted or userDetails changed", location);
  }, []);

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  const handleLogout = async () => {
    try {
      // Call the logout API
      const result = await logoutApiCall();

      // Clear localStorage regardless of API response
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token");

      // Update state
      setIsLoggedIn(false);

      // Redirect to login page
      navigate("/login");

      // Log the result for debugging
      if (result?.status) {
        console.log("Logout successful");
      } else {
        console.warn("Logout API call failed, but local logout completed");
      }
    } catch (error) {
      console.error("Error during logout:", error);
      // Still clear localStorage and redirect even if API call fails
      localStorage.removeItem("userDetails");
      localStorage.removeItem("token");
      setIsLoggedIn(false);
      navigate("/login");
    }
  };

  const handleNavScroll = (section) => {
    setMenuOpen(false);
    if (location.pathname === "/") {
      // Already on home, scroll
      const el = document.getElementById(section);
      if (el) {
        el.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      navigate("/", { state: { scrollTo: section } });
    }
  };



  return (
    <>
      <nav
        className="navbar navbar-expand-lg navbar-dark fixed-top shadow-sm"
        style={{
          backgroundColor: "rgba(15, 17, 19, 0.95)",
          backdropFilter: "blur(10px)",
        }}
      >
        <div className="container">
          <Link className="navbar-brand fw-bold" to="/">
            InterviewSupport<span className="text-primary">-AI</span>
          </Link>

          {!["/login"].includes(location.pathname) && (
            <>
              {/* Toggle button for mobile view */}
              <button
                className="navbar-toggler border-0 shadow-none"
                type="button"
                aria-expanded={!isNavCollapsed ? true : false}
                aria-label="Toggle navigation"
                onClick={handleNavCollapse}
              >
                <span className="navbar-toggler-icon"></span>
              </button>

              {/* Collapsible menu */}
              <div
                className={`${
                  isNavCollapsed ? "collapse" : ""
                } navbar-collapse`}
                id="navbarNav"
              >
                <ul className="navbar-nav mx-auto text-center py-3 py-lg-0">
                  <li className="nav-item">
                    <Link
                      to="/"
                      className={
                        "nav-link px-lg-3 " +
                        (location.pathname === "/" ? " active" : "")
                      }
                      onClick={() => setIsNavCollapsed(true)}
                    >
                      Home
                    </Link>
                  </li>
                  <li className="nav-item">
                    <Link
                      to="/contact"
                      className="nav-link px-lg-3"
                      onClick={() => setIsNavCollapsed(true)}
                    >
                      Contact
                    </Link>
                  </li>
                  {isLoggedIn ? (
                    <li className="nav-button logout-button">
                      <Button
                        id="basic-button"
                        aria-controls={open ? "basic-menu" : undefined}
                        aria-haspopup="true"
                        aria-expanded={open ? "true" : undefined}
                        onClick={handleClick}
                      >
                        <AccountCircleOutlinedIcon /> {profileName}
                      </Button>
                      <Menu
                        id="basic-menu"
                        anchorEl={anchorEl}
                        open={open}
                        onClose={handleClose}
                        slotProps={{
                          list: {
                            "aria-labelledby": "basic-button",
                          },
                        }}
                      >
                        {isLoggedIn && isAdmin && (
                          <MenuItem onClick={() => navigate("/admin")}>
                            <AdminPanelSettingsIcon
                              style={{
                                verticalAlign: "middle",
                                marginRight: 4,
                              }}
                            />
                            Admin
                          </MenuItem>
                        )}
                        <MenuItem onClick={() => navigate("/myprofile")}>
                          <PersonIcon
                            style={{ verticalAlign: "middle", marginRight: 4 }}
                          />
                          Change password
                        </MenuItem>
                        <MenuItem onClick={() => navigate("/mysubscriptions")}>
                          <SubscriptionsIcon
                            style={{ verticalAlign: "middle", marginRight: 4 }}
                          />
                          Subscriptions
                        </MenuItem>
                        <MenuItem
                          onClick={() => {
                            handleClose();
                            handleLogout();
                          }}
                        >
                          <LogoutIcon className="logout-icon" /> Logout
                        </MenuItem>
                      </Menu>
                    </li>
                  ) : (
                    <li className="nav-item">
                      <Link
                        to="/login"
                        className="nav-link px-lg-3"
                        style={{ color: "#9b5de5" }}
                      >
                        Login
                      </Link>
                    </li>
                  )}
                </ul>

                {/* Action button */}
                <div className="d-grid gap-2 d-lg-block text-center pb-3 pb-lg-0">
                  <a href={env.API_ASSETS_URL + "WindowsTransformSetup.exe"} target="_blank" rel="noopener noreferrer" style={{textDecoration: "none"}}>
                    <button
                      className="btn btn-primary rounded-pill px-4 fw-bold shadow-sm w-100"
                    >
                      Download Free
                    </button>
                  </a>
                </div>
              </div>
            </>
          )}
        </div>
      </nav>
    </>
  );
}

export default Header;
